// src/components/v2/components/AdminPortal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    TextField,
    Grid,
    Divider,
    IconButton,
    Snackbar,
    Alert as MuiAlert,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    Tooltip,
    Fade,
    Card,
    CardContent,
    Badge,
    Stack,
    alpha
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import PreviewIcon from '@mui/icons-material/Preview';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import UploadIcon from '@mui/icons-material/Upload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SectionConfig from './SectionConfig';
import LoadWizardDialog from './LoadWizardDialog';
import { createNewWizard } from '../types/schema';
import { convertToJSONSchema } from '../utils/schemaConverter';
// import { wizardApi } from './wizardApi';
import PreviewModal from './PreviewModal';
import { THEME_TYPES } from './themes';
import ThemeToggleButton from './ThemeToggleButton';
import wizardApi from '../../../service';

import LinkIcon from '@mui/icons-material/Link';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UpdateIcon from '@mui/icons-material/Update';
import PersonIcon from '@mui/icons-material/Person';

// Mini stat card component
const StatCard = ({ label, value, icon, color = 'primary' }) => (
    <Card
        elevation={0}
        sx={{
            borderLeft: 4,
            borderColor: `${color}.main`,
            bgcolor: alpha(color === 'primary' ? '#1976d2' : color === 'secondary' ? '#9c27b0' : '#2e7d32', 0.05)
        }}
    >
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Box display="flex" alignItems="center" gap={1}>
                {icon}
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                        {label}
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                        {value}
                    </Typography>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// Section tab component
const SectionTab = ({ section, index, isActive, onClick, onDelete, canDelete, disabled }) => (
    <Tooltip title={section.description || `Section ${index + 1}`} arrow placement="top">
        <Card
            elevation={isActive ? 4 : 1}
            sx={{
                minWidth: 220,
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'visible',
                backgroundColor: isActive ? 'primary.main' : 'background.paper',
                color: isActive ? 'primary.contrastText' : 'text.primary',
                transform: isActive ? 'translateY(-4px)' : 'translateY(0)',
                '&:hover': {
                    transform: isActive ? 'translateY(-4px)' : 'translateY(-2px)',
                    boxShadow: isActive ? 6 : 3,
                    backgroundColor: isActive ? 'primary.dark' : 'action.hover'
                },
                '&::before': isActive ? {
                    content: '""',
                    position: 'absolute',
                    bottom: -2,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 0,
                    height: 0,
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '8px solid',
                    borderTopColor: 'primary.main'
                } : {}
            }}
            onClick={onClick}
        >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                    <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
                        <Badge badgeContent={index + 1} color={isActive ? 'secondary' : 'default'} sx={{ mr: 2 }}>
                            <Box width={20} />
                        </Badge>
                        <Box minWidth={0} flex={1}>
                            <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                                sx={{ color: 'inherit' }}
                            >
                                {section.title || `Section ${index + 1}`}
                            </Typography>
                            <Chip
                                label={`${section.fields.length} ${section.fields.length === 1 ? 'field' : 'fields'}`}
                                size="small"
                                sx={{
                                    mt: 0.5,
                                    height: 20,
                                    fontSize: '0.7rem',
                                    bgcolor: isActive ? 'rgba(255,255,255,0.2)' : 'action.selected',
                                    color: 'inherit'
                                }}
                            />
                        </Box>
                    </Box>
                    {canDelete && (
                        <Tooltip title="Delete section" arrow>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete();
                                }}
                                disabled={disabled}
                                sx={{
                                    color: 'inherit',
                                    opacity: 0.7,
                                    '&:hover': {
                                        opacity: 1,
                                        bgcolor: 'rgba(255,255,255,0.1)'
                                    }
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </CardContent>
        </Card>
    </Tooltip>
);

const AdminPortal = () => {
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const [wizard, setWizard] = useState(createNewWizard());
    const [activeSectionIndex, setActiveSectionIndex] = useState(0);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [loadDialogOpen, setLoadDialogOpen] = useState(false);
    const [wizardsList, setWizardsList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [importJson, setImportJson] = useState('');
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    const [validationErrors, setValidationErrors] = useState({
        name: '', path: ''
    });


    // Track changes
    useEffect(() => {
        setHasUnsavedChanges(true);
    }, [wizard]);

    // Load saved wizards on component mount
    useEffect(() => {
        loadSavedWizards();

        const returnToPreview = localStorage.getItem('returnToPreview');
        if (returnToPreview) {
            try {
                const previewData = JSON.parse(returnToPreview);

                const editWizardId = searchParams.get('edit');
                if (editWizardId && editWizardId !== 'preview') {
                    setTimeout(() => {
                        handleLoadWizard(editWizardId);
                    }, 500);
                }

                localStorage.removeItem('returnToPreview');

                setNotification({
                    open: true,
                    message: 'Returned from preview. Form data was saved.',
                    severity: 'info'
                });
            } catch (err) {
                console.error('Error handling return from preview:', err);
            }
        }
    }, [location, searchParams]);

    const getAvailableFieldsInPreviousSections = (currentSectionIndex) => {
        const fields = [];
        const previousSections = wizard.sections.slice(0, currentSectionIndex);

        previousSections.forEach(section => {
            section.fields.forEach(field => {
                fields.push({
                    sectionId: section.id,
                    sectionTitle: section.title,
                    fieldName: field.name,
                    fieldLabel: field.label || field.name,
                    fieldType: field.type
                });
            });
        });

        return fields;
    };

    const loadSavedWizards = async () => {
        try {
            setLoading(true);
            const wizards = await wizardApi.getAllWizards();
            setWizardsList(wizards);
        } catch (error) {
            console.error('Error loading wizards:', error);
            setNotification({
                open: true,
                message: 'Error loading wizards',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };


    // Use useMemo for validation to prevent infinite re-renders
    const { isValid, errors } = useMemo(() => {
        const errors = {
            name: '',
            path: ''
        };

        let isValid = true;

        // Validate name
        if (!wizard.name || wizard.name.trim() === '') {
            errors.name = 'Wizard name is required';
            isValid = false;
        } else if (wizard.name.length < 3) {
            errors.name = 'Name must be at least 3 characters';
            isValid = false;
        } else if (wizard.name.length > 100) {
            errors.name = 'Name must not exceed 100 characters';
            isValid = false;
        }

        // Validate path
        if (!wizard.path || wizard.path.trim() === '') {
            errors.path = 'Path is required';
            isValid = false;
        } else if (!wizard.path.startsWith('/')) {
            errors.path = 'Path must start with /';
            isValid = false;
        } else if (wizard.path.length < 2) {
            errors.path = 'Path must be at least 2 characters';
            isValid = false;
        } else if (wizard.path.length > 255) {
            errors.path = 'Path must not exceed 255 characters';
            isValid = false;
        } else if (!/^\/[a-zA-Z0-9\-._~!$&'()*+,;=:@]+$/.test(wizard.path)) {
            errors.path = 'Path contains invalid characters. Only letters, numbers, hyphens, underscores, dots, and special URL characters are allowed';
            isValid = false;
        }

        return { isValid, errors };
    }, [wizard.name, wizard.path]); // Only recalculate when name or path changes

    // Update validation errors state when computed errors change
    useEffect(() => {
        setValidationErrors(errors);
    }, [errors]);

    // Validation function for onBlur (doesn't need to update state directly)
    const validateField = (field, value) => {
        if (field === 'name') {
            if (!value || value.trim() === '') {
                return 'Wizard name is required';
            } else if (value.length < 3) {
                return 'Name must be at least 3 characters';
            } else if (value.length > 100) {
                return 'Name must not exceed 100 characters';
            }
        } else if (field === 'path') {
            if (!value || value.trim() === '') {
                return 'Path is required';
            } else if (!value.startsWith('/')) {
                return 'Path must start with /';
            } else if (value.length < 2) {
                return 'Path must be at least 2 characters';
            } else if (value.length > 255) {
                return 'Path must not exceed 255 characters';
            } else if (!/^\/[a-zA-Z0-9\-._~!$&'()*+,;=:@]+$/.test(value)) {
                return 'Path contains invalid characters. Only letters, numbers, hyphens, underscores, dots, and special URL characters are allowed';
            }
        }
        return '';
    };

    // Update handleSave to use isValid from useMemo
    const handleSave = async () => {
        // Validate before saving
        if (!isValid) {
            setNotification({
                open: true,
                message: 'Please fix validation errors before saving',
                severity: 'error'
            });
            return;
        }

        try {
            setLoading(true);
            const jsonSchema = convertToJSONSchema(wizard);

            const saveData = {
                ...wizard,
                jsonSchema,
                updated: new Date().toISOString()
            };

            const savedWizard = await wizardApi.saveWizard(saveData);
            
            handleLoadWizard(savedWizard.id);
            setHasUnsavedChanges(false);

            setNotification({
                open: true,
                message: 'Wizard saved successfully!',
                severity: 'success'
            });

            await loadSavedWizards();

        } catch (error) {
            console.error('Error saving wizard:', error);
            const errorMessage = extractErrorMessage(error);
            setNotification({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Helper function to extract user-friendly error messages
    const extractErrorMessage = (error) => {
        if (!error) return 'An unknown error occurred';

        // If error has a response with data
        if (error.response) {
            const { status, data } = error.response;

            // Handle 400 Bad Request (validation errors)
            if (status === 400) {
                if (data && data.message) {
                    // Extract validation errors from backend
                    if (typeof data.message === 'string') {
                        return `Validation error: ${data.message}`;
                    } else if (Array.isArray(data.message)) {
                        return `Validation errors: ${data.message.join(', ')}`;
                    } else if (typeof data.message === 'object') {
                        const errors = Object.values(data.message).flat();
                        return `Validation errors: ${errors.join(', ')}`;
                    }
                    return `Validation error: ${JSON.stringify(data.message)}`;
                }
                return 'Validation failed. Please check your input.';
            }

            // Handle 409 Conflict (duplicate path)
            if (status === 409) {
                return 'A wizard with this path already exists. Please choose a different path.';
            }

            // Handle 500 Internal Server Error
            if (status === 500) {
                return 'Server error. Please try again later or contact support.';
            }

            // Generic error with status
            return `Error (HTTP ${status}): ${data?.message || 'Request failed'}`;
        }

        // If error has a message
        if (error.message) {
            // Check for common error patterns
            if (error.message.includes('Network Error')) {
                return 'Network error. Please check your connection.';
            }
            if (error.message.includes('timeout')) {
                return 'Request timeout. Please try again.';
            }
            if (error.message.includes('Failed to fetch')) {
                return 'Connection failed. Please check your network.';
            }
            return error.message;
        }

        // Fallback
        return 'An error occurred while saving the wizard.';
    };

    const handleLoadWizard = async (wizardId) => {
        console.log('Loading wizard with ID:', wizardId);
        try {
            setLoading(true);
            const loadedWizard = await wizardApi.getWizard(wizardId);
            if (loadedWizard) {
                setWizard(loadedWizard);
                setActiveSectionIndex(0);
                setLoadDialogOpen(false);
                setHasUnsavedChanges(false);

                setNotification({
                    open: true,
                    message: 'Wizard loaded successfully!',
                    severity: 'success'
                });
            } else {
                throw new Error('Wizard not found');
            }
        } catch (error) {
            setNotification({
                open: true,
                message: `Error loading wizard: ${error.message}`,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        if (wizard.sections.length > 0 &&
            window.confirm('Create new wizard? Any unsaved changes will be lost.')) {
            setWizard(createNewWizard());
            setActiveSectionIndex(0);
            setHasUnsavedChanges(false);
        }
    };

    const handleDeleteWizard = async (wizardId) => {
        if (window.confirm('Are you sure you want to delete this wizard?')) {
            try {
                setNotification({
                    open: true,
                    message: `Wizard deletion started! ${wizardId}`,
                    severity: 'success'
                });

                const success = await wizardApi.deleteWizard(wizardId);

                if (success) {
                    setNotification({
                        open: true,
                        message: 'Wizard deleted successfully!',
                        severity: 'success'
                    });

                    if (wizard.id === wizardId) {
                        setWizard(createNewWizard());
                        setActiveSectionIndex(0);
                        setHasUnsavedChanges(false);
                    }

                    await loadSavedWizards();
                }
            } catch (error) {
                setNotification({
                    open: true,
                    message: 'Error deleting wizard',
                    severity: 'error'
                });
            }
        }
    };

    const handleDuplicateWizard = async () => {
        try {
            const duplicate = {
                ...wizard,
                id: null,
                name: `${wizard.name} (Copy)`,
                created: new Date().toISOString(),
                updated: new Date().toISOString()
            };

            setNotification({
                open: true,
                message: `Wizard duplication started: ${wizard.name} (Copy)`,
                severity: 'success'
            });

            const savedWizard = await wizardApi.saveWizard(duplicate);

            setNotification({
                open: true,
                message: 'Wizard duplicated successfully!',
                severity: 'success'
            });

            await loadSavedWizards();
        } catch (error) {
            setNotification({
                open: true,
                message: 'Error duplicating wizard',
                severity: 'error'
            });
        }
    };

    const handleExportWizards = () => {
        const exportData = wizardApi.exportAllWizards();
        const blob = new Blob([exportData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `wizards_export_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        setNotification({
            open: true,
            message: 'Wizards exported successfully!',
            severity: 'success'
        });
    };

    const handleImportWizards = async () => {
        try {
            const result = wizardApi.importWizards(importJson);

            if (result.success) {
                setNotification({
                    open: true,
                    message: `Successfully imported ${result.importedCount} of ${result.total} wizards`,
                    severity: 'success'
                });
                setImportDialogOpen(false);
                setImportJson('');
                await loadSavedWizards();
            } else {
                throw new Error(result.error);
            }
        } catch (error) {
            setNotification({
                open: true,
                message: `Error importing wizards: ${error.message}`,
                severity: 'error'
            });
        }
    };

    const handleAddSection = () => {
        const newSection = {
            id: `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            title: `Section ${wizard.sections.length + 1}`,
            description: '',
            path: '',
            fields: [],
            order: wizard.sections.length
        };

        setWizard(prev => ({
            ...prev,
            sections: [...prev.sections, newSection]
        }));
        setActiveSectionIndex(wizard.sections.length);
    };

    const handleDeleteSection = (index) => {
        if (wizard.sections.length <= 1) {
            setNotification({
                open: true,
                message: 'Cannot delete the only section',
                severity: 'warning'
            });
            return;
        }

        const newSections = wizard.sections.filter((_, i) => i !== index);
        const updatedSections = newSections.map((section, idx) => ({
            ...section,
            order: idx
        }));

        setWizard(prev => ({
            ...prev,
            sections: updatedSections
        }));

        if (activeSectionIndex >= index && activeSectionIndex > 0) {
            setActiveSectionIndex(activeSectionIndex - 1);
        }
    };

    const totalFields = wizard.sections?.reduce((total, section) => {
        return total + (section?.fields?.length || 0);
    }, 0) || 0;
    const isNewWizard = wizard._backendData !== undefined ? false : true;

    console.log('Rendering AdminPortal with wizard:', wizard);

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
            <Container maxWidth="xl">
                {loading && (
                    <LinearProgress
                        sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 9999
                        }}
                    />
                )}

                {/* Header */}
                <Fade in timeout={600}>
                    <Box mb={4}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                            <Box>
                                <Typography
                                    variant="h4"
                                    fontWeight={700}
                                    gutterBottom
                                    sx={{
                                        background: THEME_TYPES === 'dark'
                                            ? 'linear-gradient(45deg, #90caf9 30%, #42a5f5 90%)'
                                            : 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent'
                                    }}
                                >
                                    Wizard Configuration Portal
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Create and manage dynamic form wizards
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">

                                {/* Settings Button (Optional) */}
                                <Tooltip title="Settings" arrow>
                                    <IconButton
                                        onClick={() => setSettingsOpen(true)}
                                        color="inherit"
                                        size="small"
                                    >
                                        {/* <SettingsIcon /> */}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Load existing wizard" arrow>
                                    <Button
                                        variant="outlined"
                                        startIcon={<FolderIcon />}
                                        onClick={() => setLoadDialogOpen(true)}
                                        size="small"
                                    >
                                        Load
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Create a copy of this wizard" arrow>
                                    <span>
                                        <Button
                                            variant="outlined"
                                            startIcon={<ContentCopyIcon />}
                                            onClick={handleDuplicateWizard}
                                            disabled={loading || isNewWizard}
                                        >
                                            Duplicate
                                        </Button>
                                    </span>

                                </Tooltip>
                                <Tooltip title="Create new wizard" arrow>
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={handleCreateNew}
                                        size="small"
                                    >
                                        New
                                    </Button>
                                </Tooltip>

                                {/* Theme Toggle Button */}
                                {/* <ThemeToggleButton /> */}

                            </Stack>
                        </Box>

                        {/* Stats Cards */}
                        <Grid container spacing={2} mb={3}>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <StatCard
                                    label="Wizard Name"
                                    value={wizard.name || 'Untitled'}
                                    icon={<EditIcon color="primary" />}
                                    color="primary"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <StatCard
                                    label="Total Sections"
                                    value={wizard.sections.length}
                                    icon={<FolderIcon color="secondary" />}
                                    color="secondary"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <StatCard
                                    label="Total Fields"
                                    value={totalFields}
                                    icon={<InfoOutlinedIcon sx={{ color: 'success.main' }} />}
                                    color="success"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <StatCard
                                    label="Status"
                                    value={isNewWizard ? 'New' : 'Saved'}
                                    icon={isNewWizard ? <AddIcon color="warning" /> : <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />}
                                    color={isNewWizard ? 'warning' : 'success'}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Fade>

                {/* Wizard Basic Info */}
                <Fade in timeout={800}>
                    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom display="flex" alignItems="center" gap={1}>
                            <EditIcon /> Basic Information
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        {/* Row 1: Name + ID side by side */}
                        <Grid container spacing={3}>
                            {/* Name - Takes 2/3 of space */}
                            <Grid item xs={12} md={8}>
                                <TextField
                                    sx={{ minWidth: 600 }}
                                    label="Wizard Name"
                                    value={wizard.name}
                                    onChange={(e) => {
                                        const newName = e.target.value;
                                        setWizard(prev => ({ ...prev, name: newName }));
                                        // Validate on change for immediate feedback
                                        const error = validateField('name', newName);
                                        setValidationErrors(prev => ({ ...prev, name: error }));
                                    }}
                                    required
                                    disabled={loading}
                                    placeholder="Enter wizard name (3-100 characters)"
                                    error={!!validationErrors.name}
                                    helperText={validationErrors.name || "Required, 3-100 characters"}
                                    InputProps={{
                                        startAdornment: <EditIcon sx={{
                                            mr: 1,
                                            color: validationErrors.name ? 'error.main' : 'action.active'
                                        }} />
                                    }}
                                />
                            </Grid>


                            {/* ID - Takes 1/3 of space */}
                            <Grid item xs={12} md={4}>
                                <TextField
                                    sx={{ minWidth: 600 }}
                                    label="Wizard ID"
                                    value={wizard.id}
                                    disabled
                                    helperText="Auto-generated ID"
                                    InputProps={{
                                        startAdornment: <InfoOutlinedIcon sx={{ mr: 1, color: 'action.active' }} />
                                    }}
                                />
                            </Grid>
                        </Grid>



                        {/* Row 2: Description (full width) */}
                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={12}>
                                <TextField
                                    sx={{ minWidth: 600 }}
                                    label="Description"
                                    value={wizard.description}
                                    onChange={(e) => setWizard(prev => ({ ...prev, description: e.target.value }))}
                                    multiline
                                    rows={3}
                                    disabled={loading}
                                    placeholder="Describe the purpose and functionality of this wizard"
                                    helperText="This description will be visible to users"
                                />
                            </Grid>
                        </Grid>

                        {/* Row 3: Path (full width) */}
                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={12}>
                                <TextField
                                    sx={{ minWidth: 600 }}
                                    label="Path *"
                                    value={wizard.path}
                                    onChange={(e) => {
                                        const newPath = e.target.value;
                                        setWizard(prev => ({ ...prev, path: newPath }));
                                        // Validate on change for immediate feedback
                                        const error = validateField('path', newPath);
                                        setValidationErrors(prev => ({ ...prev, path: error }));
                                    }}
                                    disabled={loading}
                                    placeholder="/my-wizard or /contest-form"
                                    error={!!validationErrors.path}
                                    helperText={validationErrors.path || "Required, must start with /, 2-255 characters, valid URL characters only"}
                                    InputProps={{
                                        startAdornment: <LinkIcon sx={{
                                            mr: 1,
                                            color: validationErrors.path ? 'error.main' : 'action.active'
                                        }} />
                                    }}
                                />
                            </Grid>
                        </Grid>

                        {/* Row 4: Metadata chips */}
                        {wizard.created && (
                            <Grid container spacing={3} sx={{ mt: 2 }}>
                                <Grid item xs={12}>
                                    <Box display="flex" gap={2} flexWrap="wrap">
                                        <Chip
                                            icon={<CalendarTodayIcon fontSize="small" />}
                                            label={`Created: ${new Date(wizard.created).toLocaleString()}`}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            sx={{
                                                pl: 0.5,  // Add left padding
                                                '& .MuiChip-icon': {
                                                    ml: 0.5,  // Add margin to the icon
                                                    mr: 0.5   // Add margin between icon and label
                                                }
                                            }}
                                        />
                                        {wizard.updated && (
                                            <Chip
                                                icon={<UpdateIcon fontSize="small" />}
                                                label={`Updated: ${new Date(wizard.updated).toLocaleString()}`}
                                                size="small"
                                                variant="outlined"
                                                color="secondary"
                                                sx={{
                                                    pl: 0.5,
                                                    '& .MuiChip-icon': {
                                                        ml: 0.5,
                                                        mr: 0.5
                                                    }
                                                }}
                                            />
                                        )}
                                        <Chip
                                            icon={<PersonIcon fontSize="small" />}
                                            label={`Created By: ${wizard.createdBy || 'System'}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                pl: 0.5,
                                                '& .MuiChip-icon': {
                                                    ml: 0.5,
                                                    mr: 0.5
                                                }
                                            }}
                                        />
                                        <Chip
                                            icon={<PersonIcon fontSize="small" />}
                                            label={`Modified By: ${wizard.modifiedBy || 'System'}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                pl: 0.5,
                                                '& .MuiChip-icon': {
                                                    ml: 0.5,
                                                    mr: 0.5
                                                }
                                            }}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                    </Paper>
                </Fade>

                {/* Section Management */}
                <Fade in timeout={1000}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h6" fontWeight={600} display="flex" alignItems="center" gap={1}>
                                <FolderIcon /> Sections
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleAddSection}
                                disabled={loading}
                                size="small"
                                sx={{
                                    minWidth: 140,
                                    background: hasUnsavedChanges
                                        ? 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)'
                                        : undefined
                                }}
                            >
                                Add Section
                            </Button>
                        </Box>

                        {/* Section Tabs */}
                        <Box sx={{ mb: 4 }}>
                            <Grid container spacing={2}>
                                {wizard.sections.map((section, index) => (
                                    <Grid key={section.id}>
                                        <SectionTab
                                            section={section}
                                            index={index}
                                            isActive={activeSectionIndex === index}
                                            onClick={() => setActiveSectionIndex(index)}
                                            onDelete={() => handleDeleteSection(index)}
                                            canDelete={wizard.sections.length > 1}
                                            disabled={loading}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        {/* Section Editor */}
                        {wizard.sections[activeSectionIndex] && (
                            <Box sx={{
                                p: 3,
                                bgcolor: 'background.default',
                                borderRadius: 2,
                                border: 1,
                                borderColor: 'divider'
                            }}>
                                <SectionConfig
                                    section={wizard.sections[activeSectionIndex]}
                                    onChange={(updatedSection) => {
                                        const newSections = [...wizard.sections];
                                        newSections[activeSectionIndex] = updatedSection;
                                        setWizard(prev => ({ ...prev, sections: newSections }));
                                    }}
                                    disabled={loading}
                                    availableFieldsInPreviousSections={getAvailableFieldsInPreviousSections(activeSectionIndex)}
                                    allSections={wizard.sections}
                                />
                            </Box>
                        )}
                    </Paper>
                </Fade>

                {/* Action Buttons */}
                <Fade in timeout={1200}>
                    <Box display="flex" justifyContent="space-between" mt={3} flexWrap="wrap" gap={2}>
                        <Stack direction="row" spacing={1}>

                            {wizard.id && wizard.id.startsWith('wizard_') && (
                                <Tooltip title="Permanently delete this wizard" arrow>
                                    <span>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={() => handleDeleteWizard(wizard.id)}
                                            disabled={loading}
                                        >
                                            Delete
                                        </Button>
                                    </span>

                                </Tooltip>
                            )}
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Tooltip title="Preview wizard in modal" arrow>
                                <span>
                                    <Button
                                        variant="outlined"
                                        startIcon={<PreviewIcon />}
                                        onClick={() => setPreviewModalOpen(true)}
                                        disabled={loading}
                                    >
                                        Quick Preview
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip
                                title={loading ? "Saving..." :
                                    !isValid ? "Fix validation errors before saving" :
                                        (!hasUnsavedChanges ? "No changes to save" : "Save wizard")}
                                arrow
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        startIcon={hasUnsavedChanges ? <SaveIcon /> : <CheckCircleOutlineIcon />}
                                        onClick={handleSave}
                                        size="large"
                                        disabled={loading || !hasUnsavedChanges || !isValid}
                                        sx={{
                                            minWidth: 140,
                                            background: hasUnsavedChanges && isValid
                                                ? 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)'
                                                : !isValid ? 'linear-gradient(45deg, #d32f2f 30%, #f44336 90%)' : undefined
                                        }}
                                    >
                                        {isNewWizard ? 'Save Wizard' : 'Update Wizard'}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Box>
                </Fade>

                {/* Dialogs */}
                <LoadWizardDialog
                    open={loadDialogOpen}
                    onClose={() => setLoadDialogOpen(false)}
                    onLoad={handleLoadWizard}
                    wizardsList={wizardsList}
                    onDelete={handleDeleteWizard}
                    loading={loading}
                />

                <PreviewModal
                    open={previewModalOpen}
                    onClose={() => setPreviewModalOpen(false)}
                    wizard={wizard}
                />

                {/* Import Dialog */}
                <Dialog
                    open={importDialogOpen}
                    onClose={() => setImportDialogOpen(false)}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        elevation: 8,
                        sx: { borderRadius: 2 }
                    }}
                >
                    <DialogTitle sx={{ pb: 1 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <UploadIcon color="primary" />
                            Import Wizards
                        </Box>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ pt: 3 }}>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Paste your exported wizards JSON below. Existing wizards with the same ID will be overwritten.
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={12}
                            value={importJson}
                            onChange={(e) => setImportJson(e.target.value)}
                            placeholder='[{"id": "wizard_123", "name": "Example Wizard", ...}]'
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem'
                                }
                            }}
                        />
                    </DialogContent>
                    <Divider />
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setImportDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleImportWizards}
                            variant="contained"
                            disabled={!importJson.trim()}
                            startIcon={<UploadIcon />}
                        >
                            Import
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Notification Snackbar */}
                <Snackbar
                    open={notification.open}
                    autoHideDuration={6000}
                    onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <MuiAlert
                        severity={notification.severity}
                        elevation={6}
                        variant="filled"
                        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                    >
                        {notification.message}
                    </MuiAlert>
                </Snackbar>
            </Container>
        </Box>
    );
};

export default AdminPortal;